/**
 * Backpacks on the character's back (2026-10-17).
 *
 * A bag is a rigid prop on the `chest` bone, exactly like the plate-carrier
 * vests (`TORSO_MODEL_BY_KEY`): `chest`, `spine` and the shoulders carry zero
 * animated motion in every shipped clip, so a prop parented there inherits the
 * whole hips-to-chest chain and never needs to deform.
 *
 * What differs from the vests is that ONE model serves several items. The six
 * GLBs below are shapes; an item key picks a shape and a texture, so
 * `pack_school_navy` and `pack_school_quilt` share `item_backpack-school.glb`
 * and differ only in `/models/items/packs/<itemKey>.webp`.
 *
 * ─── The waist pouch has no model ───────────────────────────────────────────
 * `model: null` is deliberate (Janeks: "without a 3d model"). The pouch is
 * equipped and counted like any bag and shows in the paperdoll and the shop
 * through its icon; nothing is drawn on the character.
 *
 * ─── Shared geometry, private materials ─────────────────────────────────────
 * `loadPropModel` hands out clones that SHARE geometry and materials with every
 * other holder of the same GLB. Setting `.map` on a shared material would
 * reskin every bag of that shape in the scene, so a textured bag gets its own
 * cloned materials, and `disposeBackpack` frees exactly those (never the
 * geometry, which the cache owns — same rule as `userData.sharedAsset`).
 *
 * `backpack-catalog.ts` (backend) is the source of truth for the key list;
 * `backpack-catalog.test.ts` holds this table to it.
 */
import * as THREE from 'three';

const ITEMS_DIR = '/models/items';

export type BackpackModelId = 'school' | 'courier' | 'sling' | 'hiking' | 'assault' | 'duffel';

export const BACKPACK_MODEL_URL: Readonly<Record<BackpackModelId, string>> = {
  school: `${ITEMS_DIR}/item_backpack-school.glb`,
  courier: `${ITEMS_DIR}/item_backpack-courier.glb`,
  sling: `${ITEMS_DIR}/item_backpack-sling.glb`,
  hiking: `${ITEMS_DIR}/item_backpack-hiking.glb`,
  assault: `${ITEMS_DIR}/item_backpack-assault.glb`,
  duffel: `${ITEMS_DIR}/item_backpack-duffel.glb`,
};

export interface BackpackVisual {
  /** `null` = no 3D model (the waist pouch). */
  model: BackpackModelId | null;
}

export const BACKPACK_VISUALS: Readonly<Record<string, BackpackVisual>> = {
  pack_pouch_olive: { model: null },
  pack_pouch_black: { model: null },
  pack_pouch_leather: { model: null },
  pack_pouch_camo: { model: null },
  pack_school_navy: { model: 'school' },
  pack_school_mustard: { model: 'school' },
  pack_school_quilt: { model: 'school' },
  pack_courier_brown: { model: 'courier' },
  pack_courier_grey: { model: 'courier' },
  pack_courier_drab: { model: 'courier' },
  pack_sling_black: { model: 'sling' },
  pack_sling_coyote: { model: 'sling' },
  pack_sling_navy: { model: 'sling' },
  pack_satchel_saddle: { model: 'courier' },
  pack_satchel_black: { model: 'courier' },
  pack_satchel_oxblood: { model: 'courier' },
  pack_courierx_multicam: { model: 'courier' },
  pack_courierx_ranger: { model: 'courier' },
  pack_courierx_urban: { model: 'courier' },
  pack_hiking_orange: { model: 'hiking' },
  pack_hiking_forest: { model: 'hiking' },
  pack_hiking_blue: { model: 'hiking' },
  pack_assault_multicam: { model: 'assault' },
  pack_assault_black: { model: 'assault' },
  pack_assault_tiger: { model: 'assault' },
  pack_assault_woodland: { model: 'assault' },
  pack_assault_urban: { model: 'assault' },
  pack_military_olive: { model: 'duffel' },
  pack_military_tarp: { model: 'duffel' },
  pack_military_camonet: { model: 'duffel' },
  pack_military_tan: { model: 'duffel' },
  pack_gilded: { model: 'hiking' },
};

export function isBackpackKey(key: string | null | undefined): boolean {
  return !!key && key in BACKPACK_VISUALS;
}

/**
 * The GLBs are authored in char-4 RIG units (the character is 0.998 tall), the
 * same as the hats, which carry this factor on their root node. `attachToBone`
 * treats a prop's geometry as metres, so without it a bag renders 1.8x too
 * small. `backpack-visuals.test.ts` holds it equal to `REFERENCE_RIG_SCALE`.
 */
export const BACKPACK_RIG_SCALE = 1.8 / 0.998;

/**
 * Where each shape sits, in the same chest-bone-local frame `TORSO_GRIP_OFFSET`
 * uses (rig units; `fixedMetreOffset` corrects other bodies' rig scale).
 *
 * The GLBs are authored in ABSOLUTE character coordinates, so the offset that
 * leaves a bag where it was authored is `-W_chest` = (0, -0.383, -0.013), the
 * reference body's chest bone in glTF space. The torso's back surface measures
 * z = -0.097 at chest height on every body (`measure_back2.py`), and a bag's
 * innermost face is at z = -0.04, so it is moved BACK by `SEAT` to rest on the
 * back instead of inside it, and UP by `RAISE` so it hangs from the shoulders.
 */
const W_CHEST: readonly [number, number, number] = [0, 0.383, 0.013];
const SEAT = 0.05;
const RAISE = 0.1;
const SEATED: readonly [number, number, number] = [-W_CHEST[0], -W_CHEST[1] + RAISE, -W_CHEST[2] - SEAT];

export const BACKPACK_OFFSET: Readonly<Record<BackpackModelId, { position: readonly [number, number, number] }>> = {
  school: { position: SEATED },
  courier: { position: SEATED },
  sling: { position: SEATED },
  hiking: { position: SEATED },
  assault: { position: SEATED },
  duffel: { position: SEATED },
};

export function backpackTextureUrl(key: string): string {
  return `${ITEMS_DIR}/packs/${key}.webp`;
}

const textureCache = new Map<string, Promise<THREE.Texture | null>>();

/** One decoded texture per item key, kept for the session (about 60 KB each). Null when the file is missing. */
export function loadBackpackTexture(key: string): Promise<THREE.Texture | null> {
  let hit = textureCache.get(key);
  if (!hit) {
    hit = new Promise<THREE.Texture | null>((resolve) => {
      new THREE.TextureLoader().load(
        backpackTextureUrl(key),
        (tex) => {
          // glTF UVs have their origin top-left; a loaded image must not be flipped to match.
          tex.flipY = false;
          tex.colorSpace = THREE.SRGBColorSpace;
          resolve(tex);
        },
        undefined,
        () => resolve(null),
      );
    });
    textureCache.set(key, hit);
  }
  return hit;
}

/** Gives every mesh in `model` its own copy of its materials, with `texture` as the colour map. */
export function dressBackpack(model: THREE.Object3D, texture: THREE.Texture): void {
  model.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const own = (m: THREE.Material): THREE.Material => {
      const c = m.clone();
      (c as THREE.MeshStandardMaterial).map = texture;
      c.userData.ownedBackpackMaterial = true;
      c.needsUpdate = true;
      return c;
    };
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(own) : own(mesh.material);
  });
}

/** Frees the per-bag materials `dressBackpack` made. Geometry and textures are cached and shared: never disposed here. */
export function disposeBackpack(model: THREE.Object3D): void {
  model.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      if (m.userData.ownedBackpackMaterial) m.dispose();
    }
  });
}

/**
 * The bag's model for an item key, textured when its texture file exists.
 * Returns null for the waist pouch and for a key that is not a backpack.
 * `loadProp` is `character-animations.ts`'s shared-asset loader, passed in so
 * this file does not import that one (it would be a cycle).
 */
export async function buildBackpackModel(
  key: string,
  loadProp: (url: string) => Promise<THREE.Object3D>,
): Promise<{ model: THREE.Object3D; offset: readonly [number, number, number] } | null> {
  const visual = BACKPACK_VISUALS[key];
  if (!visual?.model) return null;
  const [model, texture] = await Promise.all([loadProp(BACKPACK_MODEL_URL[visual.model]), loadBackpackTexture(key)]);
  if (texture) dressBackpack(model, texture);
  model.scale.setScalar(BACKPACK_RIG_SCALE);
  return { model, offset: BACKPACK_OFFSET[visual.model].position };
}
